import { useEffect, useRef, useState } from 'react';
import { ExportFormat, Info } from '@ais-ui/websockets';
import { useToast } from '@ais-ui/ui-core';
import {
  getDimensionsFromMap,
  useOpenLayersContext,
} from '@ais-ui/react-openlayers';
import { useLocalization } from '@ais-ui/locale-management';
import { AsyncJob, AsyncJobStatus, useAsyncJobs } from '@ais-ui/async-jobs';

interface Dimensions {
  center: { lat: number; lng: number };
  zoomLevel: number;
  northEast: { lat: number; lng: number };
  southWest: { lat: number; lng: number };
}

interface UseExportReportReturn {
  onExport: (format: ExportFormat) => void;
  activeExportFormat: ExportFormat | null;
  exportJobs: AsyncJob[];
}

export const useExportReport = (
  jobId: string,
  reportInfo?: Info | undefined
): UseExportReportReturn => {
  const { messages } = useLocalization();
  const { toast } = useToast();
  const { map, dataProjection, displayProjection } = useOpenLayersContext();
  const [activeExportFormat, setActiveExportFormat] =
    useState<ExportFormat | null>(null);

  // Use our AsyncJobs framework
  const { startJob, jobs } = useAsyncJobs();

  // Filter to get only export related jobs for this report
  const exportJobs = jobs.filter(
    (job) =>
      job.metadata?.type === 'report-export' && job.metadata?.reportId === jobId
  );

  // Get the active job if any
  const activeJob = exportJobs.find(
    (job) =>
      job.status === AsyncJobStatus.PENDING ||
      job.status === AsyncJobStatus.RUNNING
  );

  // Track already notified jobs with a ref
  const notifiedJobsRef = useRef(new Set());

  // When job status changes
  useEffect(() => {
    // Set activeExportFormat based on any running job
    if (activeJob) {
      setActiveExportFormat(activeJob.metadata?.format);
    } else {
      setActiveExportFormat(null);
    }

    // Look for recently completed jobs to show toast notifications
    const recentlyCompletedJob = exportJobs.find(
      (job) =>
        (job.status === AsyncJobStatus.COMPLETED ||
          job.status === AsyncJobStatus.FAILED) &&
        job.completedAt &&
        new Date().getTime() - job.completedAt.getTime() < 3000 && // Completed in the last 3 seconds
        !notifiedJobsRef.current.has(job.id) // Only show if we haven't already notified for this job
    );

    if (recentlyCompletedJob) {
      // Add this job ID to our set of notified jobs
      notifiedJobsRef.current.add(recentlyCompletedJob.id);

      if (recentlyCompletedJob.status === AsyncJobStatus.COMPLETED) {
        toast({
          className: 'export-report-success-toast',
          title: messages.genericToastTitleSuccess,
          description: messages.reportsDetailsExportReportSuccessMessage,
        });
      } else {
        toast({
          className: 'export-report-error-toast',
          title: messages.genericToastTitleError,
          description: messages.reportsDetailsExportReportErrorMessage,
          variant: 'destructive',
        });
      }
    }
  }, [exportJobs, activeJob, toast, messages]);

  const onExport = (format: ExportFormat) => {
    const dimensions: Dimensions | null = getDimensionsFromMap(
      map,
      displayProjection,
      dataProjection
    );
    if (!dimensions) {
      return;
    }

    // Show initial toast
    toast({
      title: messages.genericToastTitleInfo,
      description: messages.reportsExportStartMessages,
      variant: 'info',
    });

    // Set local state
    setActiveExportFormat(format);

    const { center, zoomLevel, northEast, southWest } = dimensions;

    startJob({
      name: `Export Report (${format})`,
      executor: async (): Promise<unknown> => {
        return new Promise<unknown>((resolve) => {
          // This promise intentionally never resolves on its own
        });
      },
      metadata: {
        type: 'report-export',
        reportId: jobId,
        format,
        reportInfo,
        centerLat: center.lat,
        centerLon: center.lng,
        zoomLevel: Math.round(zoomLevel),
        minLat: southWest.lat,
        minLon: southWest.lng,
        maxLat: northEast.lat,
        maxLon: northEast.lng,
      },
    });
  };

  return {
    onExport,
    activeExportFormat,
    exportJobs,
  };
};
